import React, { useState, useEffect, useContext } from "react";
import axios from "axios";
import { createAPIEndpoint, ENDPOINTS, BASE_URL } from "../api";
import "../assets/css/quiz.css";
import { stateContext } from "../hooks/useStateContext";
import { useLocation } from "react-router-dom";
import { HubConnectionBuilder } from "@microsoft/signalr";
import { useNavigate } from "react-router";
import Center from "./Center";
import logo from "../assets/images/logo.png";
import stopwatch from "../assets/images/stopwatch.png";
import { WelcomeMessage, QuestionMarks, Timer } from "../constants/constant";

const Quiz = () => {
  const [activeQuestion, setActiveQuestion] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState("");
  const [showResult, setShowResult] = useState(false);
  const [selectedAnswerIndex, setSelectedAnswerIndex] = useState(null);
  const [showCorrectAnswer, setShowCorrectAnswer] = useState(false);
  const [skipBtnDisabled, setSkipBtnDisabled] = useState(false);
  const [showNextBtn, setShowNextBtn] = useState(false);
  const location = useLocation();
  const user = location.state?.user.name;
  const [examStart, setExamStart] = useState(false);
  const navigate = useNavigate();
  const [timeIsUp, setTimeIsUp] = useState(false);
  const [result, setResult] = useState({
    score: 0,
    correctAnswers: 0,
    wrongAnswers: 0,
  });
  const [userAnswers, setUserAnswers] = useState([]);
  const [savedAnswers, setSavedAnswers] = useState([]);
  const [saveButtonDisabled, setSaveButtonDisabled] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [timer, setTimer] = useState(1);
  const [totalTimeTaken, setTotalTimeTaken] = useState(0);
  const [allowedTotalTime, setAllowedTotalTime] = useState(0);
  const welcomeMessage = WelcomeMessage.replace("{user}", user);
  const { context } = useContext(stateContext);
  const participantId = context.participantId;
  useEffect(() => {
    const handleBeforeUnload = () => {
      console.log(
        "User is refreshing the page. Implementing logout functionality..."
      );
      navigate("/");
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);
  useEffect(() => {
    createAPIEndpoint(ENDPOINTS.question)
      .fetch()
      .then((res) => {
        setQuestions(res.data);
        setTimer(Timer);
      })
      .catch((err) => {
        console.log(err);
      });
  }, [activeQuestion]);

  useEffect(() => {
    if (questions.length > 0) {
      setAllowedTotalTime(questions.length * Timer);
    }
  }, [questions]);

  const connection = new HubConnectionBuilder()
    .withUrl(`${BASE_URL}GetStartExam`)
    .build();

  const fetchExamStatus = () => {
    connection.on("ReceiveExamStatus", (status) => {
      setExamStart(status);
    });

    connection
      .start()
      .then(() => console.log("SignalR Connected"))
      .catch((err) => console.log("Connection failed: ", err));
  };

  useEffect(() => {
    fetchExamStatus();

    return () => {
      connection.stop();
    };
  }, []);

  useEffect(() => {
    // Check for internet connection
    const handleOnlineStatus = () => {
      if (!navigator.onLine) {
        {
          navigate("/result", { state: { value: 3 } });
        }
      }
    };

    window.addEventListener("online", handleOnlineStatus);
    window.addEventListener("offline", handleOnlineStatus);

    return () => {
      window.removeEventListener("online", handleOnlineStatus);
      window.removeEventListener("offline", handleOnlineStatus);
    };
  }, []);

  const sendScoreToServer = async (score, totalTimeTaken) => {
    try {
      const url = `${BASE_URL}api/Participant/${participantId}`;
      const response = await axios.put(url, {
        participantId: participantId,
        score: score,
        timeTaken: totalTimeTaken,
      });

      if (!response.data) {
        throw new Error("Failed to update participant score");
      }
    } catch (error) {
      console.error("Error updating participant score:", error.message);
    }
  };

  useEffect(() => {
    let timerId;

    if (examStart && totalTimeTaken >= allowedTotalTime && !showResult) {
      setTimeIsUp(true);

      timerId = setTimeout(() => {
        setShowResult(true);
      }, 1000);
    } else if (
      examStart &&
      timer > 0 &&
      activeQuestion < questions.length &&
      !showResult
    ) {
      timerId = setTimeout(() => {
        setTimer((prev) => prev - 1);
        setTotalTimeTaken((prev) => prev + 1);
        if (timer === 1) {
          onNextQuestion();
        }
      }, 1000);
    }

    return () => clearTimeout(timerId);
  }, [
    examStart,
    timer,
    activeQuestion,
    questions,
    showResult,
    totalTimeTaken,
    allowedTotalTime,
  ]);

  useEffect(() => {
    if (showResult) {
      const adjustedTotalTime = Math.min(totalTimeTaken, allowedTotalTime);
      console.log(adjustedTotalTime, result.score, "score ");
      sendScoreToServer(result.score, adjustedTotalTime);
    }
  }, [
    showResult,
    result.score,
    participantId,
    totalTimeTaken,
    allowedTotalTime,
  ]);

  if (questions.length === 0) {
    return null;
  }

  const { qnInWords, options, answer } = questions[activeQuestion];

  const onClickNext = () => {
    setTimer(Timer);
    setSkipBtnDisabled(false);
    setSelectedAnswerIndex(null);

    if (activeQuestion !== questions.length - 1) {
      setActiveQuestion((prev) => prev + 1);
    } else {
      setShowResult(true);
    }
  };

  const onNextQuestion = () => {
    // Reset other state
    setSelectedAnswer("");
    setShowCorrectAnswer(false);
    setShowNextBtn(false);
    setSkipBtnDisabled(false);
    setSelectedAnswerIndex(null);
    if (activeQuestion !== questions.length - 1) {
      setActiveQuestion((prev) => prev + 1);
    } else {
      setShowResult(true);
    }
  };
  const renderButtonLabel = () => {
    return activeQuestion === questions.length - 1 ? "Finish" : "Next";
  };
  const onSave = () => {
    const isCorrect = selectedAnswer === options[answer];

    setUserAnswers((prev) => [
      ...prev,
      {
        question: qnInWords,
        userAnswer: selectedAnswer,
        correctAnswer: options[answer],
        isCorrect: isCorrect,
      },
    ]);

    setSavedAnswers((prev) => [
      ...prev,
      {
        question: qnInWords,
        userAnswer: selectedAnswer,
        correctAnswer: options[answer],
        isCorrect: isCorrect,
      },
    ]);

    setResult((prev) =>
      isCorrect
        ? {
            ...prev,
            score: prev.score + QuestionMarks,
            correctAnswers: prev.correctAnswers + 1,
          }
        : { ...prev, wrongAnswers: prev.wrongAnswers + 1 }
    );

    //setSelectedAnswerIndex(null);
    setSaveButtonDisabled(true);
    setTimeout(() => {
      setSaveButtonDisabled(false);
    }, 10);
    setSkipBtnDisabled(true);
    setShowNextBtn(true);
    setShowCorrectAnswer(true);
  };

  const onAnswerSelected = (answer, index) => {
    setSelectedAnswerIndex(index);
    setSelectedAnswer(answer);
  };

  const addLeadingZero = (number) => (number > 9 ? number : `0${number}`);

  return (
    <>
      {examStart ? (
        <Center>
          <div className="quiz-container">
            <div className="img-container">
              <img
                src={logo}
                alt="logo"
                style={{ width: "200px", marginBottom: "20px" }}
              />
            </div>
            {timeIsUp ? (
              // <p>Time up! Please Try again </p>
              navigate("/result", { state: { value: 1 } })
            ) : !showResult ? (
              <div>
                <div className="question-info">
                  <div>
                    <span className="active-question-no">
                      {addLeadingZero(activeQuestion + 1)}
                    </span>
                    <span className="total-question">
                      /{addLeadingZero(questions.length)}
                    </span>
                  </div>
                  <div className="time-info">
                    {" "}
                    <img src={stopwatch} alt="timer" width={20} />{" "}
                    <span>{timer}s</span>{" "}
                  </div>
                </div>
                <h2>{qnInWords}</h2>
                <ul>
                  {options.map((answer, index) => (
                    <li
                      onClick={() =>
                        showCorrectAnswer ? "" : onAnswerSelected(answer, index)
                      }
                      key={answer}
                      className={`answer-option ${
                        showCorrectAnswer
                          ? answer === options[questions[activeQuestion].answer]
                            ? "correct-answer"
                            : answer !==
                                options[questions[activeQuestion].answer] &&
                              selectedAnswerIndex === index
                            ? "wrong-answer"
                            : ""
                          : selectedAnswerIndex === index
                          ? "selected-answer"
                          : ""
                      }`}
                    >
                      {answer}
                    </li>
                  ))}
                </ul>

                <div className="flex-right">
                  {!showNextBtn && (
                    <button disabled={skipBtnDisabled} onClick={onClickNext}>
                      Skip
                    </button>
                  )}

                  {!showNextBtn && (
                    <button
                      disabled={selectedAnswerIndex === null}
                      onClick={onSave}
                    >
                      Save
                    </button>
                  )}

                  {showNextBtn && (
                    <button onClick={onNextQuestion}>
                      {renderButtonLabel()}
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <div className="result">
                {navigate("/result", { state: { value: 2 } })}
              </div>
            )}
          </div>
        </Center>
      ) : (
        <Center>
          <div className="dashboard-container">
            <div className="img-container">
              <img src={logo} alt="logo" style={{ width: "200px" }} />
            </div>
            <h1 className="heading">{welcomeMessage}</h1>
          </div>
        </Center>
      )}
    </>
  );
};

export default Quiz;
